import React from 'react';
import { WorkoutExercise } from '../../../types';
import { BaseApiService } from '../../../api/services/BaseApiService';
import { useTranslation } from '../../../i18n/provider';
import styles from './ExerciseInfo.module.css';

interface ExerciseInfoProps {
  exercise: WorkoutExercise;
  onNavigateToExercise?: (exerciseId: string, templateId?: string) => void;
}

const ExerciseInfo: React.FC<ExerciseInfoProps> = ({ exercise, onNavigateToExercise }) => {
  const { t } = useTranslation();
  // Use setsData.length for completed sets when available, otherwise fall back to completedSets
  const actualCompletedSets = exercise.setsData ? exercise.setsData.length : exercise.completedSets;
  
  return (
    <div className={styles.container}>
      <div className={styles.imageContainer}>
        {/* externalUrl is dead here - the backend stopped returning it after migration 15 merged it
            into image_url (TODO.md B3/F4); imageUrl is the only field that's ever populated now. */}
        {exercise.imageUrl ? (
          <img
            src={BaseApiService.resolveImageUrl(exercise.imageUrl) as string}
            alt={`${exercise.name} preview`}
            className={styles.exerciseImage}
            loading="lazy"
          />
        ) : (
          <div className={styles.imagePlaceholder}>{t('shared.common.noImage')}</div>
        )}
        {onNavigateToExercise && (
          <button
            className={styles.infoButton}
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToExercise(exercise.id);
            }}
            title={t('shared.common.viewExerciseDetails')}
            aria-label={t('shared.common.viewExerciseInformation')}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </button>
        )}
      </div>
      <div className={styles.textContainer}>
        <h3 className={styles.title}>{exercise.name}</h3>
        <div className={styles.details}>
          <span className={styles.setsFraction}>
            {t('workout.editExercise.setsFraction', { completed: actualCompletedSets, total: exercise.sets })}
          </span>
          {/* TODO.md E, item 5: unit === 'sec' is the reliable signal here (exerciseType may not always
              be threaded onto this particular WorkoutExercise) - a hold-time set never has weight > 0
              anyway, so the weight span below already stays hidden without extra branching. */}
          {exercise.unit === 'sec' ? (
            <span>{t('workout.editExercise.holdTimeValue', { count: exercise.reps })}</span>
          ) : (
            <>
              <span>{t('workout.editExercise.repsValue', { count: exercise.reps })}</span>
              {exercise.weight > 0 && (
                <span>
                  {exercise.weight} {exercise.unit}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseInfo;
